import { Badge, Button, ButtonGroup } from '@chakra-ui/react';
import { AuditLog } from './types';
import { formatInTimeZone } from 'date-fns-tz';

export interface AuditLogSortRule {
  id: string;
  desc?: boolean;
}

const getAuditLogSortValue = (
  log: AuditLog,
  columnId: string,
): string | number | null => {
  switch (columnId) {
    case 'actor':
      return (
        log.sourceSystemUserContext?.id ||
        log.sourceSystemUserContext?.anonymousId ||
        ''
      );
    case 'action':
      return (log.action ?? '').replace(/_/g, ' ');
    case 'user':
      return log.entity?.id ?? '';
    case 'target': {
      const roles = log.extensions?.roles;
      const rolesArray = Array.isArray(roles) ? roles : [];
      return rolesArray[0]?.scope ?? '';
    }
    case 'logTime': {
      if (!log.time) return null;
      const timestamp = new Date(log.time).getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    }
    default:
      return '';
  }
};

const compareSortValues = (
  a: string | number | null,
  b: string | number | null,
): number => {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  return String(a).localeCompare(String(b), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
};

/**
 * Reorder loaded audit logs to match the table's sort rules.
 * The logs API does not accept a sort parameter, so this applies to the rows
 * already fetched (including rows appended by Load more).
 * Equal values fall back to newest log time first, unless time is the active sort.
 */
export const sortAuditLogs = (
  logs: AuditLog[],
  sortBy: AuditLogSortRule[],
): AuditLog[] => {
  if (!sortBy.length || logs.length < 2) return logs;

  const rules = sortBy.some((rule) => rule.id === 'logTime')
    ? sortBy
    : [...sortBy, { id: 'logTime', desc: true }];

  return [...logs].sort((left, right) => {
    for (const rule of rules) {
      const result = compareSortValues(
        getAuditLogSortValue(left, rule.id),
        getAuditLogSortValue(right, rule.id),
      );

      if (result !== 0) {
        return rule.desc ? -result : result;
      }
    }

    return 0;
  });
};

const AuditLogButtons = () => (
  <ButtonGroup flexDir='row' justifyContent='flex-end' size='sm' width='full'>
    <Button
      variant='outline'
      aria-label='view audit logs details'
      size='sm'
      data-testid='view-details-audit-logs-button'
      onClick={(e) => {
        e.stopPropagation();
        const trElement = (e.currentTarget as HTMLElement).closest('tr');
        trElement?.click();
      }}
    >
      View Details
    </Button>
  </ButtonGroup>
);

export const tableOptions = {
  columns: [
    {
      id: 'actor',
      Header: 'Actor',
      accessor: ({ sourceSystemUserContext }: Partial<AuditLog>) =>
        sourceSystemUserContext?.id ||
        sourceSystemUserContext?.anonymousId || (
          <Badge colorScheme='neutral'>NOT SPECIFIED</Badge>
        ),
    },
    {
      id: 'action',
      Header: 'Action',
      accessor: ({ action }: Partial<AuditLog>) =>
        action ? (
          action.replace(/_/g, ' ')
        ) : (
          <Badge colorScheme='neutral'>NOT SPECIFIED</Badge>
        ),
    },
    {
      id: 'user',
      Header: 'User',
      accessor: ({ entity }: Partial<AuditLog>) =>
        entity?.id || <Badge colorScheme='neutral'>NOT SPECIFIED</Badge>,
    },
    {
      id: 'target',
      Header: 'Target',
      accessor: ({ extensions }: Partial<AuditLog>) => {
        const roles = extensions?.roles;
        const rolesArray = Array.isArray(roles) ? roles : [];
        return (
          rolesArray[0]?.scope || (
            <Badge colorScheme='neutral'>NOT SPECIFIED</Badge>
          )
        );
      },
    },
    {
      id: 'logTime',
      Header: 'Date',
      accessor: ({ time }: Partial<AuditLog>) => {
        if (!time) return 'Invalid Date';
        const dateOnly = formatInTimeZone(
          new Date(time).getTime(),
          'UTC',
          'MMM d, yyyy',
        );
        const timeOnly = formatInTimeZone(
          new Date(time).getTime(),
          'UTC',
          'h:mm a',
        );
        return (
          <>
            <span style={{ whiteSpace: 'nowrap' }}>{dateOnly}</span>
            <br />
            <span style={{ whiteSpace: 'nowrap' }}>{timeOnly} UTC</span>
          </>
        );
      },
      sortType: (rowA: any, rowB: any) => {
        const a = new Date(rowA.original.time).getTime();
        const b = new Date(rowB.original.time).getTime();
        return a - b;
      },
    },
    {
      id: 'audit-logs-table-buttons',
      Header: '',
      accessor: () => <AuditLogButtons />,
      disableSortBy: true,
    },
  ],
};

export const exportedForTesting = { AuditLogButtons };
